import { getLogger } from '@hopdrive/sdk-core'
import type { CreateEventOptions, EventRecord, LogEventResponse } from '../../types'
import { buildEvent } from './buildEvent.js'
import { createEvent } from './createEvent.js'

/**
 * Internal implementation of logEvent — validates, builds, and persists an event.
 */
async function logEventCore(
  key: string,
  options: CreateEventOptions,
): Promise<EventRecord> {
  const { executor, safe, fireAndForget, safeWithResponse, ...rest } = options

  const eventlogInput = await buildEvent({ ...rest, key }, executor)
  const inserted = await createEvent(eventlogInput, executor)

  return {
    id: inserted.id,
    event_time_utc: inserted.event_time_utc,
    created_at_utc: inserted.created_at_utc,
    key,
    user: inserted.user,
    role: options.role,
    metadata: inserted.metadata,
    customer_id: inserted.customer_id ?? null,
    move_id: inserted.move_id ?? null,
    lane_id: inserted.lane_id ?? null,
    location_id: inserted.location_id ?? null,
    driver_id: inserted.driver_id ?? null,
    region_id: inserted.region_id ?? null,
    plate_id: inserted.plate_id ?? null,
    organization_id: inserted.organization_id ?? null,
  }
}

/**
 * Validate, build, and persist an event log entry in a single call.
 *
 * ```typescript
 * // Fire-and-forget — don't slow down the caller
 * logEvent('move.created', { user: 'admin@example.com', role: 'admin', fireAndForget: true })
 *
 * // Safe — returns null on error instead of throwing
 * const event = await logEvent('move.created', { user, role, safe: true })
 *
 * // Safe with response — returns { success, data } or { success, error }
 * const result = await logEvent('move.created', { user, role, safeWithResponse: true })
 * if (result.success) console.log(result.data)
 * else console.error(result.error)
 *
 * // Await when you need the result
 * const event = await logEvent('move.created', { user, role, move_id: 42 })
 * ```
 *
 * The key must be a dot-delimited string of lowercase letters and underscores (e.g. `"move.created"`, `"moves.cancel_status.changed"`).
 * Keys do not need to exist in the event registry. If a registry entry exists for the
 * key, metadata is validated against its Zod schema unless `skipMetadataValidation` is
 * set to `true`.
 *
 * @param key - A dot-delimited event key string (lowercase letters, underscores, and dots).
 * @param options - Event details including optional `executor` override.
 * @returns The persisted {@link EventRecord}, `null` (when `safe` and an error occurs),
 *   a {@link LogEventResponse} (when `safeWithResponse`), or `void` (when `fireAndForget`).
 *
 * @throws {@link EventValidationError} if required fields are missing, have
 *   invalid types, or metadata fails schema validation. (Not thrown when `safe`, `safeWithResponse`, or `fireAndForget`.)
 * @throws {@link RoleTypeNotFoundError} if the role name doesn't match a
 *   known role type in the database. (Not thrown when `safe`, `safeWithResponse`, or `fireAndForget`.)
 */
export function logEvent(
  key: string,
  options: CreateEventOptions & { fireAndForget: true },
): void
// eslint-disable-next-line no-redeclare
export function logEvent(
  key: string,
  options: CreateEventOptions & { safeWithResponse: true },
): Promise<LogEventResponse>
// eslint-disable-next-line no-redeclare
export function logEvent(
  key: string,
  options: CreateEventOptions & { safe: true },
): Promise<EventRecord | null>
// eslint-disable-next-line no-redeclare
export function logEvent(
  key: string,
  options: CreateEventOptions,
): Promise<EventRecord>
// eslint-disable-next-line no-redeclare
export function logEvent(
  key: string,
  options: CreateEventOptions,
): Promise<EventRecord | null | LogEventResponse> | void {
  if (options.fireAndForget) {
    logEventCore(key, options).catch((err) => {
      getLogger().warn('logEvent.failed', { key, error: err instanceof Error ? err.message : String(err) })
    })
    return
  }

  if (options.safeWithResponse) {
    return logEventCore(key, options)
      .then((data): LogEventResponse => ({ success: true as const, data }))
      .catch((err): LogEventResponse => {
        const error = err instanceof Error ? err : new Error(String(err))
        getLogger().warn('logEvent.failed', { key, error: error.message })
        return { success: false as const, error }
      })
  }

  if (options.safe) {
    return logEventCore(key, options).catch((err) => {
      getLogger().warn('logEvent.failed', { key, error: err instanceof Error ? err.message : String(err) })
      return null
    })
  }

  return logEventCore(key, options)
}
